import React from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Paper
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Flight as FlightIcon,
  Assessment as AssessmentIcon,
  Security as SecurityIcon,
  Storage as StorageIcon,
  Public as PublicIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const ATSPassengerSystemDetail: React.FC = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate('/admin/ats-system');
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mr: 2 }}
          aria-label="Back to ATS System"
        >
          Back to ATS
        </Button>
        <Box>
          <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
            ATS-Passenger (ATS-P)
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Traveler Risk Assessment and Vetting Subsystem
          </Typography>
        </Box>
      </Box>

      {/* System Overview */}
      <Card sx={{ mb: 3, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Subsystem Overview
          </Typography>
          <Typography variant="body1" paragraph>
            ATS-Passenger (ATS-P) is the traveler vetting subsystem within the Automated Targeting System, designed to 
            assess the risk of individuals seeking to enter or exit the United States via air, land, or sea. By integrating 
            passenger name records (PNR), visa data, watchlists, and travel history, ATS-P generates real-time risk scores 
            for travelers before their arrival, enabling CBP officers to focus secondary screening on high-risk individuals 
            while facilitating legitimate travel for low-risk passengers.
          </Typography>
        </CardContent>
      </Card>

      {/* 5Ws Summary */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <AssessmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Subsystem Overview: The 5Ws
      </Typography>

      <Card sx={{ mb: 4 }}>
        <TableContainer>
          <Table aria-label="ATS-Passenger 5Ws Overview">
            <TableBody>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ width: '15%', bgcolor: '#D32F2F', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="What is ATS-Passenger"
                >
                  What
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    A <strong>traveler risk assessment platform</strong> that analyzes passenger data from <strong>airlines, 
                    cruise lines, and land border crossings</strong>. Integrates <strong>Passenger Name Records (PNR), 
                    Advance Passenger Information (API), visa records, travel history, and watchlist data</strong> to generate 
                    <strong> real-time risk scores</strong> for individuals entering or exiting the U.S.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#1976D2', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Who uses ATS-Passenger"
                >
                  Who
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    Used by <strong>CBP National Targeting Center (NTC) analysts</strong>, <strong>CBP Officers (CBPOs) 
                    at ports of entry</strong>, <strong>Border Patrol agents</strong>, and <strong>pre-clearance officers 
                    at foreign airports</strong>. Also supports <strong>TSA, FBI, and intelligence community</strong> for 
                    counterterrorism and law enforcement operations.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#2E7D32', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="When was ATS-Passenger deployed"
                >
                  When
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    Deployed in <strong>2002-2003</strong> following 9/11 as part of enhanced border security. Major 
                    enhancements in <strong>2007 (PNR integration)</strong>, <strong>2013 (biometric matching)</strong>, 
                    and <strong>2019-2021 (machine learning models)</strong>. Processes traveler data <strong>72 hours 
                    before flight departure</strong> for international arrivals and <strong>real-time for land crossings</strong>.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#F57C00', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Where is ATS-Passenger deployed"
                >
                  Where
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    <strong>National Targeting Center (NTC)</strong> for centralized analysis. Deployed at <strong>328 
                    ports of entry</strong> including <strong>international airports</strong> (JFK, LAX, Miami, Atlanta), 
                    <strong>seaports</strong> (cruise terminals), <strong>land borders</strong> (San Ysidro, El Paso, 
                    Detroit), and <strong>15 pre-clearance locations</strong> in Canada, Caribbean, and Ireland.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#7B1FA2', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Why ATS-Passenger exists"
                >
                  Why
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    To provide <strong>advance risk assessment of travelers</strong> before arrival at U.S. borders, enabling 
                    <strong> proactive identification of security threats, criminals, and immigration violators</strong>. 
                    Critical for <strong>counterterrorism, border security, and immigration enforcement</strong>. Processes 
                    over <strong>400 million travelers annually</strong>, identifying high-risk individuals for secondary 
                    screening while expediting low-risk travelers.
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Core Capabilities */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Core Vetting Capabilities
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #1976D2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PersonIcon sx={{ fontSize: 40, color: '#1976D2', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                  Traveler Risk Scoring
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Comprehensive risk assessment for every traveler.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Real-Time Scoring:</strong> Risk scores generated before arrival</Typography></li>
                <li><Typography variant="body2"><strong>Watchlist Matching:</strong> FBI TSDB, No-Fly List, Selectee List integration</Typography></li>
                <li><Typography variant="body2"><strong>Travel Pattern Analysis:</strong> Unusual itineraries and destinations</Typography></li>
                <li><Typography variant="body2"><strong>Behavioral Indicators:</strong> One-way tickets, cash payments, last-minute bookings</Typography></li>
                <li><Typography variant="body2"><strong>Historical Review:</strong> Past violations, overstays, and enforcement actions</Typography></li>
                <li><Typography variant="body2"><strong>Associate Analysis:</strong> Travel companions and known associates</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #003366' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <StorageIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                  Data Integration
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Consolidates traveler data from multiple sources.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>PNR Data:</strong> Passenger Name Records from airlines and cruise lines</Typography></li>
                <li><Typography variant="body2"><strong>API Data:</strong> Advance Passenger Information (passport, visa)</Typography></li>
                <li><Typography variant="body2"><strong>ADIS:</strong> Arrival and Departure Information System records</Typography></li>
                <li><Typography variant="body2"><strong>SEVIS:</strong> Student and Exchange Visitor Information System</Typography></li>
                <li><Typography variant="body2"><strong>Visa Records:</strong> State Department visa issuance data</Typography></li>
                <li><Typography variant="body2"><strong>TECS:</strong> Treasury Enforcement Communications System</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #D32F2F' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SecurityIcon sx={{ fontSize: 40, color: '#D32F2F', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#D32F2F', fontWeight: 'bold' }}>
                  Threat Identification
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Detects multiple threat categories among travelers.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Terrorism Threats:</strong> Known or suspected terrorists (KSTs)</Typography></li>
                <li><Typography variant="body2"><strong>Criminal Activity:</strong> Wanted fugitives, gang members, human traffickers</Typography></li>
                <li><Typography variant="body2"><strong>Immigration Violations:</strong> Overstays, fraudulent documents, inadmissibles</Typography></li>
                <li><Typography variant="body2"><strong>Narcotics Smuggling:</strong> Drug couriers and trafficking indicators</Typography></li>
                <li><Typography variant="body2"><strong>Child Exploitation:</strong> Sex tourism, child trafficking indicators</Typography></li>
                <li><Typography variant="body2"><strong>Financial Crimes:</strong> Money laundering, bulk cash smuggling</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <FlightIcon sx={{ fontSize: 40, color: '#2E7D32', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                  Operational Support
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Supports field operations and enforcement decisions.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Secondary Referrals:</strong> High-risk travelers flagged for additional screening</Typography></li>
                <li><Typography variant="body2"><strong>Officer Guidance:</strong> Targeting notes and interview questions</Typography></li>
                <li><Typography variant="body2"><strong>Biometric Matching:</strong> Facial recognition and fingerprint verification</Typography></li>
                <li><Typography variant="body2"><strong>Real-Time Alerts:</strong> Immediate notifications for critical matches</Typography></li>
                <li><Typography variant="body2"><strong>Enforcement Actions:</strong> Arrest, detention, and removal tracking</Typography></li>
                <li><Typography variant="body2"><strong>Trusted Traveler:</strong> Global Entry, NEXUS, SENTRI expedited processing</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Traveler Categories */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Traveler Categories & Volumes
      </Typography>

      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'primary.main' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Traveler Type</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Entry Mode</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Annual Volume</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Vetting Focus</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell><strong>International Air</strong></TableCell>
              <TableCell>Commercial flights</TableCell>
              <TableCell>~110 million passengers</TableCell>
              <TableCell>Terrorism, watchlists, visa compliance</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Land Border (POV)</strong></TableCell>
              <TableCell>Private vehicles</TableCell>
              <TableCell>~180 million crossings</TableCell>
              <TableCell>Immigration status, criminal history</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Land Border (Pedestrian)</strong></TableCell>
              <TableCell>Walk-across</TableCell>
              <TableCell>~50 million crossings</TableCell>
              <TableCell>Document fraud, inadmissibility</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Sea (Cruise)</strong></TableCell>
              <TableCell>Cruise ships</TableCell>
              <TableCell>~15 million passengers</TableCell>
              <TableCell>Criminal activity, immigration violations</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Crew Members</strong></TableCell>
              <TableCell>Air/sea crew</TableCell>
              <TableCell>~20 million entries</TableCell>
              <TableCell>Smuggling, unauthorized work</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Trusted Travelers</strong></TableCell>
              <TableCell>Global Entry lanes</TableCell>
              <TableCell>~25 million entries</TableCell>
              <TableCell>Expedited processing, low-risk</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* System Scale */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <PublicIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        System Scale & Performance
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #1976D2' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                400M+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Annual Travelers
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Passengers processed
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #D32F2F' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#D32F2F', fontWeight: 'bold' }}>
                2M+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Secondary Screenings
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                High-risk referrals
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                24/7
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Operations
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Real-time vetting
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #F57C00' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                328
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Ports of Entry
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Nationwide deployment
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Key Benefits */}
      <Card sx={{ mb: 4, bgcolor: '#e3f2fd', border: '2px solid #1976D2' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1976D2' }}>
            Operational Impact
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Advance Screening:</strong> Risk assessment before traveler arrival</Typography></li>
                <li><Typography variant="body2"><strong>Threat Detection:</strong> Identify terrorists, criminals, and violators</Typography></li>
                <li><Typography variant="body2"><strong>Resource Optimization:</strong> Focus screening on high-risk travelers</Typography></li>
                <li><Typography variant="body2"><strong>Travel Facilitation:</strong> Expedite low-risk passengers</Typography></li>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Intelligence Integration:</strong> Multi-agency watchlist coordination</Typography></li>
                <li><Typography variant="body2"><strong>Biometric Verification:</strong> Confirm traveler identity</Typography></li>
                <li><Typography variant="body2"><strong>Border Security:</strong> Prevent inadmissible aliens from entry</Typography></li>
                <li><Typography variant="body2"><strong>Data-Driven Vetting:</strong> Evidence-based risk assessment</Typography></li>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Integration Points */}
      <Card sx={{ mb: 4, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Key System Integrations
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Chip label="TECS (Law Enforcement)" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Treasury Enforcement System</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="FBI TSDB (Watchlist)" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Terrorist Screening Database</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="ADIS (Travel Records)" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Arrival/Departure System</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="SEVIS (Students)" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Student/Exchange Visitor</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="State Visa System" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Visa issuance records</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="IDENT (Biometrics)" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">DHS biometric database</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="Global Entry/NEXUS" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Trusted traveler programs</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="Airline/Carrier APIs" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">PNR and API data feeds</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Container>
  );
};

export default ATSPassengerSystemDetail;
